import z from 'zod';
import i18n from '@/common/translations/i18n';

const t = i18n.t;

export const picklistOptionSchema = z.object({
  id: z.number().optional(),
  name: z
    .string()
    .trim()
    .min(1, {
      message: t(($) => $.common.errors.required, {
        fieldName: t(($) => $.picklistTable.name),
      }),
    })
    .max(50, {
      message: t(($) => $.common.errors.maxCharacterLimit, {
        limit: 50,
        fieldName: t(($) => $.picklistTable.name),
      }),
    }),
  color: z.string().length(6),
  archived: z.boolean(),
});

export const customFieldFormSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, {
      message: t(($) => $.common.errors.required, {
        fieldName: t(($) => $.common.settings.itemTypes.sheet.name),
      }),
    })
    .max(50, {
      message: t(($) => $.common.errors.maxCharacterLimit, {
        limit: 50,
        fieldName: t(($) => $.common.settings.itemTypes.sheet.name),
      }),
    }),

  type: z.enum([
    'CHECKBOX',
    'CURRENCY',
    'DATE',
    'ITEM',
    'LINK',
    'MULTI_CUSTOMER',
    'MULTI_ITEM',
    'MULTI_LINK',
    'MULTI_PICKLIST',
    'MULTI_TEXT',
    'MULTI_USER',
    'MULTILINE_TEXT',
    'NOTE',
    'NUMERIC',
    'PICKLIST',
    'TEXT',
    'USER',
  ]),

  currencyUnit: z.string().optional(),

  picklistOptions: z.array(picklistOptionSchema).optional(),

  itemTypeAssociationMode: z.enum(['ALL', 'CUSTOM']),

  associatedItemTypeIds: z.array(z.number()),
});

export const createCustomFieldFormSchema = (existingNames: string[], currentFieldName?: string) => {
  return customFieldFormSchema
    .refine(
      (data) => {
        const normalizedName = data.name.trim().toLowerCase();
        const normalizedCurrentName = currentFieldName?.trim().toLowerCase();

        const isNameUnchanged = normalizedCurrentName && normalizedName === normalizedCurrentName;
        if (isNameUnchanged) {
          return true;
        }

        const isDuplicate = existingNames.some(
          (name) => name.trim().toLowerCase() === normalizedName,
        );

        return !isDuplicate;
      },
      {
        message: t(($) => $.common.errors.nameMustBeUnique),
        path: ['name'],
      },
    )
    .refine(
      (data) => data.itemTypeAssociationMode !== 'CUSTOM' || data.associatedItemTypeIds.length > 0,
      {
        message: t(($) => $.common.errors.required, {
          fieldName: t(($) => $.customFieldsTable.appliesTo),
        }),
        path: ['associatedItemTypeIds'],
      },
    );
};

export type CustomFieldFormValues = z.infer<typeof customFieldFormSchema>;

export type CustomFieldFormPicklistOption = z.infer<typeof picklistOptionSchema>;

export type CustomFieldPicklistOptionWithId = CustomFieldFormPicklistOption & {
  id: number;
};
